/* eslint-disable no-bitwise */

import { invariant } from '../../util/invariant';
import type { StructuredLogger } from '@mimir/primitive/structuredLogger';
import { ConsistentUuidV4Generator } from '../../util/consistentUuidV4Generator';
import type { IndexEntry, IndexTableSegment } from './indexTableSegment';
import { calculateIndexTableSegmentKlvSize, INDEX_ENTRY_FLAGS_B_FRAME, INDEX_ENTRY_FLAGS_P_FRAME, INDEX_ENTRY_FLAG_BACKWARD_PREDICTION_BIT, INDEX_ENTRY_FLAG_FORWARD_PREDICTION_BIT, INDEX_ENTRY_FLAG_RANDOM_ACCESS_BIT, INDEX_ENTRY_FLAG_SEQUENCE_HEADER_BIT, serializeIndexTableSegment } from './indexTableSegment';
import type { KLV } from './klv';
import { calculateKagSize, calculateKlvTotalSize, createKlvFill } from './klv';
import type { FrameType, MimirIndexEntry, MimirIndexVariableFrameSize, MimirIndexFixedFrameSize, MimirIndexFile } from './mimirIndexFile';
import { calculateSubClipIndexInfo } from './mimirIndexFileUtilities';
import type { SubClipIndexInfo } from './mimirIndexFileUtilities';
import type { PartitionPack } from './partitionPack';
import { calculatePaddedPartitionPackSize, serializePartitionPack } from './partitionPack';
import type { RandomIndexPackEntry } from './randomIndexPack';
import type { PartitionPackAndHeaders } from './reconstructionUtils';
import { rewriteHeader } from './rewriteHeader';
import { MxfDictionary } from './mxfDictionary';
import cloneDeep from 'lodash/cloneDeep';
import type { MxfRewrapDependencies } from './rewrapDependencies';
import type { ResourceLocation } from '../../io/resource';
import type { Timecode } from './../../util/timecode';

export interface MxfSegmentPartition {
  type: 'partition'
  partitionPack: PartitionPack,
  headers: KLV[],
  indexTableSegment: IndexTableSegment | undefined,
  targetOffset: number,
}

export function mxfSegmentPartitionToKlvs(segment: MxfSegmentPartition): KLV[] {
  const klvs : KLV[] = [];
  const kagSize = segment.partitionPack.kagSize;
  const partitionKlv = serializePartitionPack(segment.partitionPack);
  klvs.push(serializePartitionPack(segment.partitionPack));
  {
    const fillKlv = createKlvFill(calculateKlvTotalSize(partitionKlv), kagSize);
    if (fillKlv !== undefined) {
      klvs.push(fillKlv);
    }
  }
  klvs.push(...segment.headers);

  {
    const fillKlv = createKlvFill(segment.partitionPack.headerByteCount, kagSize);
    if (fillKlv !== undefined) {
      klvs.push(fillKlv);
    }
  }

  if (segment.indexTableSegment !== undefined) {
    const dictionary = new MxfDictionary();
    const indexKlv = serializeIndexTableSegment(segment.indexTableSegment, dictionary);
    klvs.push(indexKlv);
    {
      const fillKlv = createKlvFill(calculateKlvTotalSize(indexKlv), kagSize);
      if (fillKlv !== undefined) {
        klvs.push(fillKlv);
      }
    }
  }
  return klvs;
}

export interface MxfSegmentRandomIndexPack {
  type: 'random_index_pack'
  randomIndexPack: RandomIndexPackEntry[],
  targetOffset: number,
}

export interface MxfSegmentEssence {
  type: 'essence',
  highRes: ResourceLocation,
  startByteOffset: number,
  endByteOffset: number,
  targetOffset: number,
}

export type MxfSegment =
  MxfSegmentPartition |
  MxfSegmentRandomIndexPack |
  MxfSegmentEssence;

export interface MxfRewrapPlan {
  segments: MxfSegment[],
  /**
   *  The compatibility hash of the generated MXF file. May be null for single
   *  MXF rewrap operations.
   */
  compatibilityHash: string | null,

  /**
   *  Mimir index file for the generated MXF file
   */
  mimirIndexFile: MimirIndexFile,
}

export interface MxfRewrapSettings {
  overrideStartTimecode: Timecode | null,
}

export function planMxfRewrap(
  structuredLogger: StructuredLogger,
  rewrapDependencies: MxfRewrapDependencies,
  settings?: MxfRewrapSettings,
): MxfRewrapPlan {
  // First we determine what frames from the original files we need to include to ensure that
  // all the frames in the requested frame range is decodable. I.e dealing with long gop cases
  // etc.

  structuredLogger.log({
    level: 'info',
    message: 'rewrapping from MXF sources',
    sources: rewrapDependencies.sources.map((source) => ({ ...source.highRes, bytes: undefined }))
  });

  const sourceInfos : SourceInfo[] =
    rewrapDependencies.sources.map((source) => {
      const subClipIndexInfo =
        calculateSubClipIndexInfo(
          structuredLogger,
          source.index,
          source.inPoint + source.origin,
          source.outPoint + source.origin,
          source.fileSize,
        );

      return {
        highRes: source.highRes,
        compatibilityHash: source.compatibilityHash,
        fileSize: source.fileSize,
        subClipIndexInfo,
      };
    });

  invariant(sourceInfos.length > 0, 'Expected at least one source');

  const indexType = sourceInfos[0].subClipIndexInfo.mimirIndex.type;

  const compatibilityHash = sourceInfos[0].compatibilityHash;
  if (sourceInfos.length > 1) {
    for (const sourceInfo of sourceInfos) {
      if (sourceInfo.compatibilityHash === null) {
        throw new Error('MXF rewrap with multiple inputs require rewrap compatibility hash for all items');
      }
      if (sourceInfo.compatibilityHash !== compatibilityHash) {
        throw new Error('MXF rewrap with multiple inputs require that all clips have the same compatibility hash');
      }
    }
  }

  for (let i = 0; i < sourceInfos.length; i++) {
    const sourceInfo = sourceInfos[i];

    invariant(
      sourceInfo.subClipIndexInfo.mimirIndex.type === indexType,
      'Cannot have a mix of variable and fixed frame size sources',
    );

    if (sourceInfo.subClipIndexInfo.mimirIndex.type === 'fixed_frame_size') {
      invariant(
        sourceInfo.subClipIndexInfo.leadInFrameCount === 0,
        'Did not expect leadInFrameCount for fixed frame source',
      );
    } else {
      const first = (i === 0);
      const last = (i === sourceInfos.length - 1);
      invariant(
        (sourceInfo.subClipIndexInfo.leadInFrameCount === 0) || first,
        'Only the first source have an in point in the middle of a GOP',
      );

      const postRollFrameCount =
        sourceInfo.subClipIndexInfo.leadInFrameCount +
        sourceInfo.subClipIndexInfo.validFrameCount -
        sourceInfo.subClipIndexInfo.mimirIndex.entries.length;

      invariant(
        (postRollFrameCount === 0) || last,
        'Only the last source can have an out point in the middle of a GOP',
      );
    }
  }

  let segmentsAndIndex : Omit<MxfRewrapPlan, 'compatibilityHash'>;
  switch (indexType) {
    case 'variable_frame_size':
      segmentsAndIndex =
        planVariableFrameSizeMxfRewrap(
          structuredLogger,
          settings,
          sourceInfos,
          rewrapDependencies.templates.header,
          rewrapDependencies.templates.indexTableSegment,
          rewrapDependencies.templates.footer,
        );
      break;
    case 'fixed_frame_size':
      segmentsAndIndex =
        planFixedFrameSizeMxfRewrap(
          structuredLogger,
          settings,
          sourceInfos,
          rewrapDependencies.templates.header,
          rewrapDependencies.templates.indexTableSegment,
          rewrapDependencies.templates.footer,
        );
      break;
  }

  return { ...segmentsAndIndex, compatibilityHash };
}

interface SourceInfo {
  highRes: ResourceLocation,
  fileSize: number,
  subClipIndexInfo: SubClipIndexInfo,
  compatibilityHash: string | null,
}

function planVariableFrameSizeMxfRewrap(
  structuredLogger: StructuredLogger,
  settings: MxfRewrapSettings | undefined,
  sourceInfos: SourceInfo[],
  header: PartitionPackAndHeaders,
  indexTableSegmentTemplate: IndexTableSegment,
  footer: PartitionPackAndHeaders,
): Omit<MxfRewrapPlan, 'compatibilityHash'> {
  const clippedIndices : MimirIndexVariableFrameSize[] =
    sourceInfos.map((s) => {
      invariant(s.subClipIndexInfo.mimirIndex.type === 'variable_frame_size', 'Index no longer variable after rewrite?');
      return s.subClipIndexInfo.mimirIndex;
    });

  const kagSize = header.partitionPack.kagSize;

  const { removedFramesCount, leadInFrameCount } = sourceInfos[0].subClipIndexInfo;

  const totalFrameCount = sourceInfos
    .reduce((count, sourceInfo) => count + sourceInfo.subClipIndexInfo.validFrameCount, 0);

  structuredLogger.log({ level: 'info', message: 'Rewriting MXF headers' });
  header =
    rewritePartitionPackAndHeaders(
      structuredLogger,
      header,
      removedFramesCount,
      leadInFrameCount,
      totalFrameCount,
      settings?.overrideStartTimecode ?? null,
    );

  // Some MXF files have footers without any headers
  if (footer.headers.length > 0) {
    structuredLogger.log({ level: 'info', message: `Rewriting ${footer.headers.length} header KLVs in the MXF footer` });
    footer =
      rewritePartitionPackAndHeaders(
        structuredLogger,
        footer,
        removedFramesCount,
        leadInFrameCount,
        totalFrameCount,
        settings?.overrideStartTimecode ?? null,
      );
  }

  structuredLogger.log({ level: 'info', message: 'Creating index table segments' });

  const sourceEntries = clippedIndices.map((index) => index.entries).flat();

  // First we plan how the body partitions will be split and generate the index table
  // segments for each body partition.
  const bodyPartitionInfos : PartitionInfo[] =
    planVariableFramePartitions(sourceEntries, indexTableSegmentTemplate, header.partitionPack);

  // The last partition of the array will become the footer partition
  const [footerPartitionInfo] = bodyPartitionInfos.splice(-1);

  structuredLogger.log({ level: 'info', message: 'Building MXF segments' });

  // At this point we have all the required information to build up the plan of
  // MXF segments. The overall structure of the MXF file will be:
  //
  // | Header partition 1 |
  // | Metadata           |
  // | BodyPartition 1   |
  // | Essence 1          |
  // | BodyPartition 2    |
  // | Index Table 1      |
  // | Essence 2          |
  // | BodyPartition 3    |
  // | Index Table 2      |
  // | Essence 3          |
  //    (...)
  // | Footer partition |
  // | Index Table N - 1  |
  // | Random index pack |
  //

  const segments : MxfSegment[] = [];

  const headerSegment : MxfSegmentPartition = {
    type: 'partition',
    partitionPack: {
      ...header.partitionPack,
      thisPartition: 0,
      previousPartition: 0,
      footerPartition: footerPartitionInfo.fileOffset,
      indexByteCount: 0,
      indexSid: 0,
      bodySid: 0,
      bodyOffset: 0,
    },
    headers: header.headers,
    indexTableSegment: undefined,
    targetOffset: 0,
  };

  const bodySid = indexTableSegmentTemplate.bodySid;
  const indexSid = indexTableSegmentTemplate.indexSid;

  segments.push(headerSegment);

  let sourceInfoIndex = 0;
  let sourceIndex = clippedIndices[sourceInfoIndex];
  let sourceFrameNumber = 0;

  const outputIndexEntries : MimirIndexEntry[] = [];

  for (const bodyPartitionInfo of bodyPartitionInfos) {
    const bodyPartition : MxfSegmentPartition = {
      type: 'partition',
      partitionPack: {
        ...header.partitionPack,
        partitionType: 'body',
        closed: true,
        complete: true,
        headerByteCount: 0,
        bodySid,
        thisPartition: bodyPartitionInfo.fileOffset,
        footerPartition: footerPartitionInfo.fileOffset,
        bodyOffset: bodyPartitionInfo.essenceOffset,
        // The first body partition has no index table
        indexSid: bodyPartitionInfo.indexTableSegment !== null ? indexSid : 0,
        previousPartition: bodyPartitionInfo.previousFileOffset,
        indexByteCount: bodyPartitionInfo.indexTableByteCount,
      },
      headers: [],
      indexTableSegment: bodyPartitionInfo.indexTableSegment ?? undefined,
      targetOffset: bodyPartitionInfo.fileOffset,
    };

    segments.push(bodyPartition);

    let essenceSegment : MxfSegmentEssence | null = null;

    let targetOffset =
      bodyPartition.targetOffset +
      calculatePaddedPartitionPackSize(headerSegment.partitionPack) +
      bodyPartition.partitionPack.headerByteCount +
      bodyPartition.partitionPack.indexByteCount;

    for (let j = 0; j < bodyPartitionInfo.frameCount; j++) {
      if (sourceFrameNumber === sourceIndex.entries.length) {
        sourceInfoIndex++;
        sourceFrameNumber = 0;
        invariant(
          sourceInfoIndex < clippedIndices.length,
          'The partition planing should set the last partition frame count so that we never gets to this point'
        );
        sourceIndex = clippedIndices[sourceInfoIndex];
      }
      const sourceEntry = sourceIndex.entries[sourceFrameNumber];
      outputIndexEntries.push({
        ...sourceEntry,
        frameByteOffset: targetOffset,
      });

      if (essenceSegment !== null) {
        if (essenceSegment.endByteOffset === sourceEntry.frameByteOffset) {
          essenceSegment.endByteOffset += sourceEntry.frameSize;
        } else {
          segments.push(essenceSegment);
          essenceSegment = null;
        }
      }

      if (essenceSegment === null) {
        essenceSegment = {
          type: 'essence',
          highRes: sourceInfos[sourceInfoIndex].highRes,
          startByteOffset: sourceEntry.frameByteOffset,
          endByteOffset: sourceEntry.frameByteOffset + sourceEntry.frameSize,
          targetOffset,
        };
      }

      targetOffset += sourceEntry.frameSize;

      sourceFrameNumber += 1;
    }
    if (essenceSegment !== null) {
      segments.push(essenceSegment);
    }
  }

  const footerByteSize =
    calculateKagSize(footer.headers.reduce((totalSize, klv) => totalSize + calculateKlvTotalSize(klv), 0), kagSize);

  const footerSegment : MxfSegmentPartition = {
    type: 'partition',
    partitionPack: {
      ...footer.partitionPack,
      thisPartition: footerPartitionInfo.fileOffset,
      previousPartition: footerPartitionInfo.previousFileOffset,
      footerPartition: footerPartitionInfo.fileOffset,
      headerByteCount: footerByteSize,
      indexByteCount: footerPartitionInfo.indexTableByteCount,
      bodyOffset: 0,
      indexSid,
    },
    headers: footer.headers,
    indexTableSegment: footerPartitionInfo.indexTableSegment ?? undefined,
    targetOffset: footerPartitionInfo.fileOffset,
  };

  segments.push(footerSegment);

  const randomIndexPack : RandomIndexPackEntry[] = [];
  // Header Partition Pack
  randomIndexPack.push({
    bodySid: header.partitionPack.bodySid,
    byteOffset: 0,
  });

  for (const bodyPartitionInfo of bodyPartitionInfos) {
    randomIndexPack.push({
      bodySid,
      byteOffset: bodyPartitionInfo.fileOffset,
    });
  }

  randomIndexPack.push({
    bodySid: footer.partitionPack.bodySid,
    byteOffset: footerPartitionInfo.fileOffset,
  });

  const randomIndexPackSegment : MxfSegmentRandomIndexPack = {
    type: 'random_index_pack',
    randomIndexPack,
    targetOffset:
      footerSegment.targetOffset +
      calculatePaddedPartitionPackSize(footerSegment.partitionPack) +
      footerSegment.partitionPack.headerByteCount +
      footerSegment.partitionPack.indexByteCount,
  };

  segments.push(randomIndexPackSegment);

  const firstPartitionWithIndexTable = segments.find((segment) => {
    return (segment.type === 'partition') && (segment.partitionPack.indexByteCount > 0);
  });

  invariant(
    (firstPartitionWithIndexTable !== undefined) &&
    (firstPartitionWithIndexTable.type === 'partition'),
    'expected at least one partition with index table'
  );


  const firstIndexTableSize = firstPartitionWithIndexTable.partitionPack.indexByteCount;
  const firstIndexTableOffset =
    firstPartitionWithIndexTable.partitionPack.thisPartition +
    calculatePaddedPartitionPackSize(firstPartitionWithIndexTable.partitionPack) +
    firstPartitionWithIndexTable.partitionPack.headerByteCount;

  const mimirIndexFile : MimirIndexFile = {
    header: {
      firstIndexTableSize,
      firstIndexTableOffset,
    },
    index: {
      type: 'variable_frame_size',
      entries: outputIndexEntries,
    }
  };

  return { segments, mimirIndexFile };
}

/**
 * This function will generate the MXF index table segments for a list of
 * frames expressed as a Mimir Variable Frame size index.
 */
interface PartitionInfo {
  /**
   * Absolute file offset of the partition
   */
  fileOffset: number,

  /**
   * Absolute file offset of the previous partition
   */
  previousFileOffset: number,

  /**
   * The essence offset of the first essence in the partition
   */
  essenceOffset: number,

  /**
   * The total number of bytes of essence in the body partition
   */
  essenceByteCount: number,

  /**
   * Index table segment that will be stored in this body partition
   * partition. I.e the index table that indexes the essence contained
   * in the previous partition. If null that means that this is the
   * first body partition
   */
  indexTableSegment: IndexTableSegment | null,

  /**
   * The size of the index table segment that will be stored in this partition.
   * I.e the index table that indexes the essence contained in the previous
   * partition
   */
  indexTableByteCount: number,

  /**
   * Number of frames in this partition
   */
  frameCount: number,
}

function planVariableFramePartitions(
  sourceEntries: MimirIndexEntry[],
  template: IndexTableSegment,
  headerPartitionPack: PartitionPack,
): PartitionInfo[] {
  const { headerByteCount, kagSize } = headerPartitionPack;
  const partitionInfos : PartitionInfo[] = [];

  /**
   * FFmpeg seems to generate partitions of 240 edit units. Who are we to
   * question the FFmpeg developers?
   */
  const framesPerBodyPartition = 240;

  const paddedHeaderByteSize = calculateKagSize(headerByteCount, kagSize);

  // This was generated once using generateRandomSfc32Seed()
  const seed = {
    a: 987157576,
    b: 2679612400,
    c: 3679042299,
    d: 3567812384
  };

  const uuidGenerator = new ConsistentUuidV4Generator(seed);

  let fileOffset = calculatePaddedPartitionPackSize(headerPartitionPack) + paddedHeaderByteSize;
  let frameNumber = 0;
  let essenceOffset = 0;
  let done = false;
  let previousIndexTableByteCount = 0;
  let previousIndexTableSegment : IndexTableSegment | null = null;
  let previousFileOffset = 0;

  while (!done) {
    const indexTableSegment : IndexTableSegment = {
      ...template,
      // Nablet's MXF explorer seems to complain if the instanceUid is the same
      // for multiple IndexTableSegments. Premiere seems to also have unique
      // instanceUid for each indexTableSegment. FFmpeg has the same instanceUid
      // for all indexTableSegments.
      instanceUid: uuidGenerator.uuidv4(),
      indexStartPosition: frameNumber,
      indexDuration: 0,
      indexEntries: [],
    };

    let essenceByteCount = 0;
    const firstEssenceOffset = essenceOffset;
    for (let i = 0; i < framesPerBodyPartition; i++) {
      if (frameNumber === sourceEntries.length) {
        done = true;
        break;
      }
      const sourceEntry : MimirIndexEntry = sourceEntries[frameNumber];
      const entry : IndexEntry = {
        temporalOffset: sourceEntry.temporalOffset,
        keyFrameOffset: sourceEntry.keyFrameOffset,
        flags: mimirIndexEntryToMxfIndexEntryFlags(sourceEntry),
        streamOffset: essenceOffset,
        sliceOffsets: sourceEntry.sliceOffsets,
        posTable: [],
      };
      indexTableSegment.indexEntries.push(entry);
      essenceOffset += sourceEntry.frameSize;
      essenceByteCount += sourceEntry.frameSize;

      frameNumber += 1;
    }
    indexTableSegment.indexDuration = indexTableSegment.indexEntries.length;

    partitionInfos.push({
      fileOffset,
      previousFileOffset,
      essenceOffset: firstEssenceOffset,
      essenceByteCount,
      indexTableSegment: previousIndexTableSegment,
      indexTableByteCount: previousIndexTableByteCount,
      frameCount: indexTableSegment.indexDuration,
    });

    previousFileOffset = fileOffset;

    fileOffset += calculatePaddedPartitionPackSize(headerPartitionPack);
    fileOffset += previousIndexTableByteCount;
    fileOffset += essenceByteCount;

    const indexTableSize = calculateIndexTableSegmentKlvSize(indexTableSegment);
    previousIndexTableSegment = indexTableSegment;
    previousIndexTableByteCount = calculateKagSize(indexTableSize, kagSize);
  }

  partitionInfos.push({
    fileOffset,
    previousFileOffset,
    essenceOffset,
    essenceByteCount: 0,
    indexTableSegment: previousIndexTableSegment,
    indexTableByteCount: previousIndexTableByteCount,
    frameCount: 0,
  });

  return partitionInfos;
}

function planFixedFrameSizeMxfRewrap(
  structuredLogger: StructuredLogger,
  settings: MxfRewrapSettings | undefined,
  sourceInfos: SourceInfo[],
  header: PartitionPackAndHeaders,
  indexTableSegmentTemplate: IndexTableSegment,
  footer: PartitionPackAndHeaders,
): Omit<MxfRewrapPlan, 'compatibilityHash'> {
  const kagSize = header.partitionPack.kagSize;

  const { removedFramesCount } = sourceInfos[0].subClipIndexInfo;

  const totalFrameCount = sourceInfos
    .reduce((count, sourceInfo) => count + sourceInfo.subClipIndexInfo.validFrameCount, 0);

  structuredLogger.log({ level: 'info', message: 'Rewriting MXF headers' });
  header =
    rewritePartitionPackAndHeaders(
      structuredLogger,
      header,
      removedFramesCount,
      0,
      totalFrameCount,
      settings?.overrideStartTimecode ?? null,
    );

  // Some MXF files have footers without any headers
  if (footer.headers.length > 0) {
    structuredLogger.log({ level: 'info', message: `Rewriting ${footer.headers.length} header KLVs in the MXF footer` });
    footer =
      rewritePartitionPackAndHeaders(
        structuredLogger,
        footer,
        removedFramesCount,
        0,
        totalFrameCount,
        settings?.overrideStartTimecode ?? null,
      );
  }

  const sourceIndices : MimirIndexFixedFrameSize[] =
    sourceInfos.map((sourceInfo) => {
      const mimirIndex = sourceInfo.subClipIndexInfo.mimirIndex;
      invariant(mimirIndex.type === 'fixed_frame_size', 'Expected all sources to be fixed frame size');
      return mimirIndex;
    });

  let targetOffset = 0;

  const fixedFrameSize = sourceIndices[0].fixedFrameSize;

  const indexTable = cloneDeep(indexTableSegmentTemplate);
  indexTable.indexStartPosition = 0;
  indexTable.indexDuration = totalFrameCount;
  indexTable.editUnitByteCount = fixedFrameSize;

  const indexTableSize = calculateIndexTableSegmentKlvSize(indexTable);

  const paddedIndexTableSize = calculateKagSize(indexTableSize, kagSize);

  const headerSegment : MxfSegmentPartition = {
    type: 'partition',
    partitionPack: {
      ...header.partitionPack,
      bodySid: indexTable.bodySid,
      indexSid: indexTable.indexSid,
      indexByteCount: paddedIndexTableSize,
    },
    headers: header.headers,
    indexTableSegment: indexTable,
    targetOffset,
  };

  targetOffset += calculatePaddedPartitionPackSize(headerSegment.partitionPack);
  targetOffset += header.partitionPack.headerByteCount;

  const firstIndexTableOffset = targetOffset;

  targetOffset += paddedIndexTableSize;

  const firstFrameByteOffset = targetOffset;

  const firstIndex = sourceInfos[0].subClipIndexInfo.mimirIndex;

  invariant(
    firstIndex.type === 'fixed_frame_size',
    'Mimir index file is not fixed frame size variant',
  );

  for (const sourceIndex of sourceIndices) {
    invariant(
      sourceIndex.fixedFrameSize === fixedFrameSize,
      `Expected all sources to have a fixedFrameSize of ${sourceIndices[0].fixedFrameSize}, but ${sourceIndex.fixedFrameSize} was found`,
    );
  }

  const maxChunkSize = 1024 * 1024 * 50;

  const essenceSegments : MxfSegmentEssence[] = [];
  for (let j = 0; j < sourceInfos.length; j++) {
    let essenceSegment : MxfSegmentEssence | null = null;
    const sourceInfo = sourceInfos[j];
    const sourceIndex = sourceIndices[j];
    const firstFrameByteOffset = sourceIndex.firstFrameByteOffset;
    for (let i = 0; i < sourceInfo.subClipIndexInfo.validFrameCount; i++) {
      const start = firstFrameByteOffset + (fixedFrameSize * i);
      const end = start + fixedFrameSize;
      if (essenceSegment !== null) {
        if ((essenceSegment.endByteOffset === start) && ((end - essenceSegment.startByteOffset) < maxChunkSize)) {
          essenceSegment.endByteOffset = end;
        } else {
          essenceSegments.push(essenceSegment);
          essenceSegment = null;
        }
      }

      if (essenceSegment === null) {
        essenceSegment = {
          type: 'essence',
          highRes: sourceInfo.highRes,
          startByteOffset: start,
          endByteOffset: end,
          targetOffset,
        };
      }

      targetOffset += end - start;
    }

    if (essenceSegment !== null) {
      essenceSegments.push(essenceSegment);
    }
  }

  const footerOffset = targetOffset;

  headerSegment.partitionPack.footerPartition = footerOffset;

  const footerSegment : MxfSegmentPartition = {
    type: 'partition',
    partitionPack: {
      ...footer.partitionPack,
      thisPartition: footerOffset,
      footerPartition: footerOffset,
      previousPartition: 0,
      bodySid: 0,
      indexSid: 0,
      indexByteCount: 0,
    },
    headers: footer.headers,
    indexTableSegment: undefined,
    targetOffset,
  };

  targetOffset += calculatePaddedPartitionPackSize(footerSegment.partitionPack);
  targetOffset += footer.partitionPack.headerByteCount;

  const randomIndexPackSegment : MxfSegmentRandomIndexPack = {
    type: 'random_index_pack',
    randomIndexPack: [
      { bodySid: indexTable.bodySid, byteOffset: 0 },
      { bodySid: 0, byteOffset: footerOffset },
    ],
    targetOffset,
  };


  return {
    segments: [
      headerSegment,
      ...essenceSegments,
      footerSegment,
      randomIndexPackSegment
    ],
    mimirIndexFile: {
      header: {
        firstIndexTableSize: headerSegment.partitionPack.indexByteCount,
        firstIndexTableOffset,
      },
      index: {
        type: 'fixed_frame_size',
        firstFrameByteOffset,
        fixedFrameSize,
      },
    }
  };
}

// End of public API

function rewritePartitionPackAndHeaders(
  structuredLogger: StructuredLogger,
  partitionPackAndHeaders: PartitionPackAndHeaders,
  removedFrameCount: number,
  origin: number,
  duration: number,
  startTimecode: Timecode | null,
): PartitionPackAndHeaders {
  const kagSize = partitionPackAndHeaders.partitionPack.kagSize;
  const headers =
    rewriteHeader(
      kagSize,
      structuredLogger,
      partitionPackAndHeaders.headers,
      removedFrameCount,
      origin,
      duration,
      startTimecode,
    );

  const headerByteCount = calculateKagSize(headers.reduce((totalSize, klv) => totalSize + 16 + klv.length.bytesUsed + klv.length.length, 0), kagSize);

  return {
    partitionPack: {
      ...partitionPackAndHeaders.partitionPack,
      headerByteCount,
    },
    headers,
  };
}

function frameTypeToIndexEntryFlags(frameType: FrameType): number {
  switch (frameType) {
    case 'I':
      return INDEX_ENTRY_FLAG_SEQUENCE_HEADER_BIT;
    case 'p':
      return INDEX_ENTRY_FLAG_FORWARD_PREDICTION_BIT | INDEX_ENTRY_FLAGS_P_FRAME;
    case 'b':
      return INDEX_ENTRY_FLAG_FORWARD_PREDICTION_BIT | INDEX_ENTRY_FLAG_BACKWARD_PREDICTION_BIT | INDEX_ENTRY_FLAGS_B_FRAME;
    case 'B':
      return INDEX_ENTRY_FLAG_BACKWARD_PREDICTION_BIT | INDEX_ENTRY_FLAGS_B_FRAME;
    default:
      throw new Error(`Invalid frame type: ${frameType}`);
  }
}

function randomAccessToIndexEntryFlags(randomAccess: boolean): number {
  return (randomAccess ? INDEX_ENTRY_FLAG_RANDOM_ACCESS_BIT : 0);
}

function mimirIndexEntryToMxfIndexEntryFlags(entry: MimirIndexEntry): number {
  return frameTypeToIndexEntryFlags(entry.frameType) |
    randomAccessToIndexEntryFlags(entry.randomAccess);
}
